import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LogoMark from "./LogoMark";

const LINKS = [
    { to: "/", label: "Home", id: "nav-home" },
    { to: "/about", label: "About", id: "nav-about" },
    { to: "/services", label: "Services", id: "nav-services" },
    { to: "/approach", label: "Our Approach", id: "nav-approach" },
    { to: "/insights", label: "Insights", id: "nav-insights" },
];

const EASE = [0.16, 1, 0.3, 1];

export default function Nav() {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-black/10 bg-white/90">
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-20 md:px-12">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-3 text-ink"
                    aria-label="ELCAYIL CONSULTING LTD. home"
                >
                    <LogoMark className="h-7 w-auto md:h-8" />
                    <span className="flex flex-col leading-none">
                        <span className="text-[15px] font-extrabold tracking-[0.08em] md:text-base">
                            ELCAYIL
                        </span>
                        <span className="eyebrow mt-1 text-[9px] text-ink/50">
                            Consulting Ltd.
                        </span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.id}
                            className={({ isActive }) =>
                                `link-line text-[13px] font-medium tracking-wide ${
                                    isActive
                                        ? "is-active text-brand"
                                        : "text-ink/60 hover:text-ink"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-cta-contact"
                        className="rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold text-white transition-colors duration-300 hover:bg-brand"
                    >
                        Speak With Us
                    </Link>
                </nav>

                <button
                    data-testid="nav-menu-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen((v) => !v)}
                    className="relative z-[60] flex h-10 w-10 items-center justify-center lg:hidden"
                >
                    <span className="flex flex-col items-end gap-1.5">
                        <span
                            className={`h-[1.5px] bg-ink transition-all duration-300 ${
                                open
                                    ? "w-6 translate-y-[3.5px] rotate-45"
                                    : "w-6"
                            }`}
                        />
                        <span
                            className={`h-[1.5px] bg-ink transition-all duration-300 ${
                                open
                                    ? "w-6 -translate-y-[3.5px] -rotate-45"
                                    : "w-4"
                            }`}
                        />
                    </span>
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 flex flex-col justify-center bg-white px-8 lg:hidden"
                    >
                        <nav className="flex flex-col gap-2">
                            {[
                                ...LINKS,
                                { to: "/contact", label: "Contact", id: "nav-contact" },
                            ].map((l, i) => (
                                <span key={l.to} className="block overflow-hidden">
                                    <motion.span
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{
                                            duration: 0.7,
                                            delay: 0.05 + i * 0.06,
                                            ease: EASE,
                                        }}
                                        className="block"
                                    >
                                        <NavLink
                                            to={l.to}
                                            data-testid={l.id}
                                            className={({ isActive }) =>
                                                `font-serif-d block py-2 text-4xl ${
                                                    isActive
                                                        ? "italic text-brand"
                                                        : "text-ink"
                                                }`
                                            }
                                        >
                                            {l.label}
                                        </NavLink>
                                    </motion.span>
                                </span>
                            ))}
                        </nav>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="eyebrow mt-14 text-ink/40"
                        >
                            Excellent in Performance.
                        </motion.p>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
